(ns io.daskul.webapp.aggregates.subs
  "Generic CRUD subscriptions for aggregates.

   Provides standard re-frame subscriptions that accept aggregate keyword as parameter.
   Subscription names use noun-style (NOT verb-style like events).

   ## Naming Convention: Events vs Subscriptions

   Events (actions/verbs):  ::list, ::get, ::create, ::patch, ::delete
   Subs (data/nouns):       ::all, ::by-id, ::current, ::loading?, ::error"
  (:require
   [re-frame.core :as rf]
   [io.daskul.webapp.aggregates.db :as db]))

;; ============================================================================
;; Aggregate Data
;; ============================================================================

;; All records (denormalize: join IDs -> entities)
(rf/reg-sub ::all
  (fn [db [_ agg-key]]
    (let [ids (get-in db (db/list-ids-path agg-key))
          entities (get-in db (db/entities-path agg-key))]
      (mapv entities ids))))

;; By ID (direct O(1) lookup from normalized entities)
(rf/reg-sub ::by-id
  (fn [db [_ agg-key id]]
    (get-in db (db/entity-path agg-key id))))

;; Current record (use current-id)
(rf/reg-sub ::current
  (fn [db [_ agg-key]]
    (let [current-id (get-in db (db/current-id-path agg-key))]
      (when current-id
        (get-in db (db/entity-path agg-key current-id))))))

;; ============================================================================
;; Loading States
;; ============================================================================

(rf/reg-sub ::loading?
  (fn [db [_ agg-key]]
    (get-in db (db/loading-path agg-key) false)))

(rf/reg-sub ::creating?
  (fn [db [_ agg-key]]
    (get-in db (db/creating-path agg-key) false)))

(rf/reg-sub ::updating?
  (fn [db [_ agg-key]]
    (get-in db (db/updating-path agg-key) false)))

(rf/reg-sub ::deleting?
  (fn [db [_ agg-key]]
    (get-in db (db/deleting-path agg-key) false)))

;; ============================================================================
;; Error States
;; ============================================================================

(rf/reg-sub ::error
  (fn [db [_ agg-key]]
    (get-in db (db/error-path agg-key))))

(rf/reg-sub ::field-error
  (fn [db [_ agg-key field-key]]
    (get-in db (conj (db/error-path agg-key) :field-errors field-key))))

;; ============================================================================
;; Schema
;; ============================================================================

(rf/reg-sub ::schema
  (fn [db [_ agg-key operation]]
    (get-in db (db/schema-path agg-key operation))))

(rf/reg-sub ::schema-loading?
  (fn [db [_ agg-key]]
    (get-in db (db/schema-loading-path agg-key) false)))

;; ============================================================================
;; Lookups
;; ============================================================================

(rf/reg-sub ::lookup-options
  (fn [db [_ agg-key field-key]]
    (get-in db (db/lookup-path agg-key field-key :items) [])))

(rf/reg-sub ::lookup-loading?
  (fn [db [_ agg-key field-key]]
    (get-in db (db/lookup-path agg-key field-key :loading?) false)))

;; ============================================================================
;; Pagination
;; ============================================================================

(rf/reg-sub ::pagination
  (fn [db [_ agg-key]]
    (get-in db (db/pagination-path agg-key))))

;; ============================================================================
;; Counts (segmented filter badges)
;; ============================================================================

(rf/reg-sub ::counts
  (fn [db [_ agg-key group-by-field]]
    (get-in db (db/counts-path agg-key group-by-field))))

;; ============================================================================
;; FSM Transitions
;; ============================================================================

(rf/reg-sub ::transitioning?
  (fn [db [_ agg-key]]
    (get-in db (db/transitioning-path agg-key) false)))

(rf/reg-sub ::transition-event
  (fn [db [_ agg-key]]
    (get-in db (db/transition-event-path agg-key))))

(rf/reg-sub ::transition-errors
  (fn [db [_ agg-key]]
    (get-in db (db/transition-errors-path agg-key))))

(rf/reg-sub ::transition-field-error
  (fn [db [_ agg-key field-key]]
    (get-in db (conj (db/transition-errors-path agg-key) :field-errors field-key))))

;; ============================================================================
;; Embedded Relationships
;; ============================================================================

(rf/reg-sub ::embedded-items
  (fn [db [_ agg-key parent-id rel-name]]
    (get-in db (db/embedded-path agg-key parent-id rel-name :items) [])))

(rf/reg-sub ::embedded-loading?
  (fn [db [_ agg-key parent-id rel-name]]
    (get-in db (db/embedded-path agg-key parent-id rel-name :loading?) false)))

(rf/reg-sub ::embedded-error
  (fn [db [_ agg-key parent-id rel-name]]
    (get-in db (db/embedded-path agg-key parent-id rel-name :error))))

(rf/reg-sub ::embedded-saving?
  (fn [db [_ agg-key parent-id rel-name]]
    (get-in db (db/embedded-path agg-key parent-id rel-name :saving?) false)))

(rf/reg-sub ::embedded-form-error
  (fn [db [_ agg-key parent-id rel-name]]
    (get-in db (db/embedded-path agg-key parent-id rel-name :form-error))))

(rf/reg-sub ::embedded-save-success?
  (fn [db [_ agg-key parent-id rel-name]]
    (get-in db (db/embedded-path agg-key parent-id rel-name :save-success?) false)))

;; ============================================================================
;; Child Aggregates
;; ============================================================================

(rf/reg-sub ::child-items
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :items) [])))

;; `::child-items` defaults to [] when the key is absent, so it cannot tell
;; "fetched, and there are none" from "not fetched yet". That is exactly the
;; ambiguity F-060 wants removed from the count badge — a relation with no
;; badge could mean either, and they are different facts.
(rf/reg-sub ::child-loaded?
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (some? (get-in db (db/child-path parent-agg-key parent-id child-agg-key :items)))))

(rf/reg-sub ::child-loading?
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :loading?) false)))

(rf/reg-sub ::child-error
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :error))))

(rf/reg-sub ::child-saving?
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :saving?) false)))

(rf/reg-sub ::child-form-error
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :form-error))))

(rf/reg-sub ::child-field-error
  (fn [db [_ parent-agg-key parent-id child-agg-key field-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :form-error :field-errors field-key))))

(rf/reg-sub ::child-save-success?
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :save-success?) false)))

(rf/reg-sub ::child-deleting?
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :deleting?) false)))

(rf/reg-sub ::child-transitioning?
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :transitioning?) false)))

(rf/reg-sub ::child-transition-event
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (get-in db (db/child-path parent-agg-key parent-id child-agg-key :transition-event))))
